import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Trash2Icon } from "lucide-react";

import { useSuppliers, type Supplier } from "@/api/suppliers";
import { DataTable, type ColumnDef } from "@/components/data-table/DataTable";
import { useDataTableState } from "@/components/data-table/useDataTableState";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";

import { DeleteSupplierDialog } from "./DeleteSupplierDialog";
import { SupplierDialog } from "./SupplierDialog";

export default function SuppliersPage() {
  const { t } = useTranslation();

  const ds = useDataTableState({
    defaultSort: { id: "name", desc: false },
    defaultPageSize: 50,
    filterDeps: [],
  });

  const query = useSuppliers({
    search: ds.debouncedSearch || undefined,
    ordering: ds.orderingParam,
    limit: ds.limit,
    offset: ds.offset,
  });

  const [editing, setEditing] = useState<Supplier | null>(null);
  const [deleting, setDeleting] = useState<Supplier | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const columns = useMemo<ColumnDef<Supplier>[]>(
    () => [
      {
        accessorKey: "code",
        header: t("suppliers.columnCode"),
        size: 160,
        enableSorting: true,
      },
      {
        accessorKey: "name",
        header: t("suppliers.columnName"),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        size: 48,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon-sm"
              data-testid="delete"
              aria-label={t("common.delete")}
              onClick={() => setDeleting(row.original)}
            >
              <Trash2Icon className="text-muted-foreground size-4" />
            </Button>
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("suppliers.title")}
        actions={
          <Button onClick={() => setNewOpen(true)}>{t("suppliers.new")}</Button>
        }
      />

      <DataTable<Supplier>
        {...ds.tableProps}
        data={query.data?.results ?? []}
        totalCount={query.data?.count ?? 0}
        isLoading={query.isLoading}
        columns={columns}
        searchPlaceholder={t("suppliers.searchPlaceholder")}
        emptyLabel={t("suppliers.empty")}
        getRowId={(r) => String(r.id)}
        onRowClick={(row) => setEditing(row)}
      />

      <SupplierDialog
        open={newOpen}
        supplier={editing}
        onClose={() => {
          setNewOpen(false);
          setEditing(null);
        }}
      />
      <DeleteSupplierDialog
        supplier={deleting}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
